/**
 * IndexedDB wrapper for storing song recordings (takes) locally
 * Separate database from audio/versions to avoid migration conflicts
 */

const DB_NAME = "LyricToolkitRecordings";
const DB_VERSION = 1;
const STORE_NAME = "songRecordings";

const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
        store.createIndex("songId", "songId", { unique: false });
      }
    };
  });
};

/**
 * Save a recording to IndexedDB
 * Converts Blob to ArrayBuffer before storing for reliable mobile storage
 */
export const saveRecording = async (
  songId,
  takeNumber,
  blob,
  metadata = {},
) => {
  const id = `${songId}-take-${takeNumber}`;

  // Convert Blob to ArrayBuffer before creating transaction
  console.log("🔄 Converting recording blob to ArrayBuffer...");
  const arrayBuffer = await blob.arrayBuffer();
  console.log("✅ Converted to ArrayBuffer, size:", arrayBuffer.byteLength);

  const recordingData = {
    id,
    songId: String(songId),
    takeNumber,
    label: metadata.label || `Take ${takeNumber}`,
    arrayBuffer,
    filename: metadata.filename || `take-${takeNumber}.webm`,
    size: blob.size,
    duration: metadata.duration || 0,
    type: blob.type || "audio/webm",
    shiftOffsetSec: metadata.shiftOffsetSec || 0,
    createdAt: new Date().toISOString(),
  };

  const db = await openDB();
  const tx = db.transaction([STORE_NAME], "readwrite");
  const store = tx.objectStore(STORE_NAME);

  return new Promise((resolve, reject) => {
    tx.onerror = () => {
      console.error("❌ Recording transaction error:", tx.error);
      reject(tx.error);
    };
    const request = store.put(recordingData);
    request.onsuccess = () => {
      console.log("✅ Recording saved to IndexedDB:", id);
      resolve(recordingData);
    };
    request.onerror = () => {
      console.error("❌ Recording put error:", request.error);
      reject(request.error);
    };
  });
};

/**
 * List all recordings for a song (metadata only, no audio data)
 */
export const listRecordings = async (songId) => {
  const db = await openDB();
  const tx = db.transaction([STORE_NAME], "readonly");
  const store = tx.objectStore(STORE_NAME);
  const index = store.index("songId");

  return new Promise((resolve, reject) => {
    const request = index.getAll(String(songId));
    request.onsuccess = () => {
      const recordings = (request.result || [])
        .map((r) => ({
          id: r.id,
          songId: r.songId,
          takeNumber: r.takeNumber,
          label: r.label,
          filename: r.filename,
          size: r.size,
          duration: r.duration,
          type: r.type,
          shiftOffsetSec: r.shiftOffsetSec || 0,
          createdAt: r.createdAt,
        }))
        .sort((a, b) => a.takeNumber - b.takeNumber);
      resolve(recordings);
    };
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get a specific recording with its audio data
 */
export const getRecording = async (songId, takeNumber) => {
  const id = `${songId}-take-${takeNumber}`;
  const db = await openDB();
  const tx = db.transaction([STORE_NAME], "readonly");
  const store = tx.objectStore(STORE_NAME);

  return new Promise((resolve, reject) => {
    const request = store.get(id);
    request.onsuccess = () => {
      if (request.result) {
        console.log("✅ Recording retrieved from IndexedDB:", id);
        resolve(request.result);
      } else {
        console.warn("⚠️ Recording not found in IndexedDB:", id);
        resolve(null);
      }
    };
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get the next available take number for a song
 */
export const getNextTakeNumber = async (songId) => {
  const recordings = await listRecordings(songId);
  if (recordings.length === 0) return 1;
  return Math.max(...recordings.map((r) => r.takeNumber)) + 1;
};

/**
 * Count recordings for a song
 */
export const countRecordings = async (songId) => {
  const recordings = await listRecordings(songId);
  return recordings.length;
};

/**
 * Count total recordings across all songs
 */
export const countAllRecordings = async () => {
  const db = await openDB();
  const tx = db.transaction([STORE_NAME], "readonly");
  const store = tx.objectStore(STORE_NAME);

  return new Promise((resolve, reject) => {
    const request = store.count();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Delete a specific recording
 */
export const deleteRecording = async (songId, takeNumber) => {
  const id = `${songId}-take-${takeNumber}`;
  const db = await openDB();
  const tx = db.transaction([STORE_NAME], "readwrite");
  const store = tx.objectStore(STORE_NAME);

  return new Promise((resolve, reject) => {
    const request = store.delete(id);
    request.onsuccess = () => {
      console.log("✅ Recording deleted from IndexedDB:", id);
      resolve(true);
    };
    request.onerror = () => reject(request.error);
  });
};

/**
 * Delete all recordings for a song (used when song is deleted)
 */
export const deleteAllRecordings = async (songId) => {
  const recordings = await listRecordings(songId);
  if (recordings.length === 0) return 0;

  const db = await openDB();
  const tx = db.transaction([STORE_NAME], "readwrite");
  const store = tx.objectStore(STORE_NAME);

  for (const r of recordings) {
    store.delete(r.id);
  }

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve(recordings.length);
    tx.onerror = () => reject(tx.error);
  });
};

/**
 * Update a recording's label
 */
export const updateRecordingLabel = async (songId, takeNumber, newLabel) => {
  const id = `${songId}-take-${takeNumber}`;
  const db = await openDB();

  // Read the existing record first
  const tx1 = db.transaction([STORE_NAME], "readonly");
  const store1 = tx1.objectStore(STORE_NAME);

  const existing = await new Promise((resolve, reject) => {
    const request = store1.get(id);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  if (!existing) throw new Error("Recording not found");

  existing.label = newLabel;

  const tx2 = db.transaction([STORE_NAME], "readwrite");
  const store2 = tx2.objectStore(STORE_NAME);

  return new Promise((resolve, reject) => {
    const request = store2.put(existing);
    request.onsuccess = () => resolve(existing);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Create a blob URL for recording playback
 */
export const createRecordingBlobURL = (recordingData) => {
  try {
    if (recordingData.arrayBuffer) {
      const blob = new Blob([recordingData.arrayBuffer], {
        type: recordingData.type || "audio/webm",
      });
      return URL.createObjectURL(blob);
    }
    throw new Error("No audio data in recording");
  } catch (error) {
    console.error("❌ Error creating recording blob URL:", error);
    throw error;
  }
};

/**
 * Update the shift offset for a recording
 */
export const updateShiftOffset = async (songId, takeNumber, shiftOffsetSec) => {
  const id = `${songId}-take-${takeNumber}`;
  const db = await openDB();

  const tx1 = db.transaction([STORE_NAME], "readonly");
  const store1 = tx1.objectStore(STORE_NAME);

  const existing = await new Promise((resolve, reject) => {
    const request = store1.get(id);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  if (!existing) throw new Error("Recording not found");

  existing.shiftOffsetSec = shiftOffsetSec;

  const tx2 = db.transaction([STORE_NAME], "readwrite");
  const store2 = tx2.objectStore(STORE_NAME);

  return new Promise((resolve, reject) => {
    const request = store2.put(existing);
    request.onsuccess = () => resolve(existing);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Replace the audio blob for an existing recording (used by punch-in splice)
 * Preserves label, shiftOffsetSec, createdAt — only updates audio data + duration
 */
export const replaceRecordingBlob = async (
  songId,
  takeNumber,
  blob,
  duration,
) => {
  const id = `${songId}-take-${takeNumber}`;
  const db = await openDB();

  const tx1 = db.transaction([STORE_NAME], "readonly");
  const existing = await new Promise((resolve, reject) => {
    const request = tx1.objectStore(STORE_NAME).get(id);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  if (!existing) throw new Error("Recording not found");

  const arrayBuffer = await blob.arrayBuffer();
  existing.arrayBuffer = arrayBuffer;
  existing.size = blob.size;
  existing.type = blob.type || "audio/wav";
  if (duration != null) existing.duration = duration;

  const tx2 = db.transaction([STORE_NAME], "readwrite");
  return new Promise((resolve, reject) => {
    const request = tx2.objectStore(STORE_NAME).put(existing);
    request.onsuccess = () => {
      console.log("✅ Recording audio replaced in IndexedDB:", id);
      resolve(existing);
    };
    request.onerror = () => reject(request.error);
  });
};

export default {
  saveRecording,
  listRecordings,
  getRecording,
  getNextTakeNumber,
  countRecordings,
  countAllRecordings,
  deleteRecording,
  deleteAllRecordings,
  updateRecordingLabel,
  updateShiftOffset,
  createRecordingBlobURL,
  replaceRecordingBlob,
};
